export function setPageTitle(title: string | null = 'Hotjar'): void {
  if (title === null) {
    return;
  }

  document.title = title;
}

export function setFavicon(href: string): void {
  const faviconElem: HTMLLinkElement | null = document.querySelector('#favicon');

  if (!faviconElem || !href) {
    return;
  }

  faviconElem.href = href;
}

interface BodyClassConfig {
  pageState: string;
  pageType: string;
  loggedIn: boolean;
  sidebarMinimized: boolean;
  showInterface: boolean;
}

/**
 * This function allows us to set properties in the <head> and <body> that were originally set through
 * AngularJS. We should not rely on body classes in React code since these won't be set once we move
 * entirely to React. Instead stick to using styled components to set specific styles for your components
 * rather than using global styles and namespaces.
 */
export function setBodyClasses({
  pageState,
  pageType,
  loggedIn,
  sidebarMinimized,
  showInterface,
}: BodyClassConfig): void {
  const body = document.querySelector('body');

  if (!body) {
    return;
  }

  const classList = Array.from(body.classList);
  const pageClass = classList.find((className) => className.includes('page-'));
  const pageTypeClass = classList.find((className) => className.includes('pagetype-'));
  const userClass = classList.find((className) => className.includes('user-'));

  if (pageClass) {
    body.classList.replace(pageClass, `page-${pageState}`);
  } else {
    body.classList.add(`page-${pageState}`);
  }

  if (pageTypeClass) {
    body.classList.replace(pageTypeClass, `pagetype-${pageType}`);
  } else {
    body.classList.add(`pagetype-${pageType}`);
  }

  if (userClass) {
    body.classList.replace(userClass, `user-${loggedIn}`);
  } else {
    body.classList.add(`user-${loggedIn}`);
  }

  if (sidebarMinimized) {
    body.classList.add('sidebar-minimized');
  }

  if (!showInterface) {
    body.classList.add('hide-framework');
  }
}

/**
 * Sets the `useraccess` attribute on the body element. We shouldn't need to do this in React since we can
 * manage user access in a more explicit manner in React.
 */
export function setUserAccessBodyProp(access: 'read' | 'write' | 'admin'): void {
  const body = document.querySelector('body');

  if (!body) {
    return;
  }

  body.setAttribute('useraccess', access);
}
